import type { DayOrigin, DayRemoval, PtoDecision, WorkerBoardView, WorkerBridgeCallout, WorkerRunResult } from '../types.js'
import { PRIORITY_LABELS } from './preferencesText.js'

/**
 * How a day the worker holds came to them, beyond the released schedule: the board's own kinds, a Clinical Bridge
 * callout they accepted, or `against` - a PTO day denied or a day off taken to cover a short unit, worked against their ask.
 */
export type DayMarkKind = DayOrigin['kind'] | 'bridge' | 'against'

/** A shift taken off the worker since the release, and whether it is offered back to them now. */
export interface RemovedShift extends DayRemoval {
  /** A low-census seat they may take back until `recallUntil`. */
  offeredBack: boolean
  recallUntil: string | null
}

/**
 * Everything the worker's calendar says about one date beyond its shifts (worker calendar design, 26 October 2026):
 * the fill says whose the day is, the mark how it got there, and coral what needs the worker or went against them.
 */
export interface DayMark {
  date: string
  /** How the day became the worker's after the release; null for a day as released, or none. */
  origin: { kind: DayMarkKind; at: string | null; note: string | null } | null
  removed: RemovedShift | null
  /** An open shift offered to this worker in turn now: the one kind that asks for an answer. */
  offered: boolean
  /** Asked for at the bid and given to someone ranked ahead. */
  lost: boolean
  pto: PtoDecision['status'] | null
  /** The unit, when the worker's shift that day is not on their home unit. */
  awayUnitId: string | null
}

/** One month's sources: the released result, the board as the worker reads it, and the Bridge callouts they were asked about. */
export interface DayMarkSources {
  result: WorkerRunResult | null
  board: WorkerBoardView | null
  bridgeCallouts: readonly WorkerBridgeCallout[]
}

/** A Bridge callout's start, as the shift type a calendar day labels: before noon day, before eight evening, else night. */
function shiftTypeAt(start: string): string {
  const hour = Number(start.slice(0, 2))
  return hour >= 5 && hour < 12 ? 'day' : hour >= 12 && hour < 20 ? 'evening' : 'night'
}

/** A Clinical Bridge shift the worker accepted, shaped as the calendar's scheduled shifts are. */
export interface BridgeShift {
  date: string
  shiftType: string
  startTime: string
  endTime: string
  unitId: string
}

/** The Clinical Bridge shifts the worker accepted, as calendar shifts: they are the worker's though no schedule holds them. */
export function bridgeShiftsOf(callouts: readonly WorkerBridgeCallout[]): BridgeShift[] {
  return callouts.filter((callout) => callout.result === 'yours').map((callout) => ({ date: callout.shift.date, shiftType: shiftTypeAt(callout.shift.start), startTime: callout.shift.start, endTime: callout.shift.end, unitId: callout.shift.unitId }))
}

function emptyMark(date: string): DayMark {
  return { date, origin: null, removed: null, offered: false, lost: false, pto: null, awayUnitId: null }
}

/** Every date of one month that has something to mark, in date order. */
export function dayMarksOf({ result, board, bridgeCallouts }: DayMarkSources): DayMark[] {
  const marks = new Map<string, DayMark>()
  const mark = (date: string, change: Partial<DayMark>): void => {
    marks.set(date, { ...(marks.get(date) ?? emptyMark(date)), ...change })
  }
  const held = board?.assignments ?? result?.awarded ?? []
  const workingOn = new Set(held.map((entry) => entry.date))

  // Against the worker's ask first, so a later change to the same day (a swap, say) is the mark that shows.
  for (const decision of result?.ptoDecisions ?? []) {
    if (decision.status === 'denied' && workingOn.has(decision.date)) {
      mark(decision.date, { origin: { kind: 'against', at: null, note: 'Your PTO request was denied: a working day.' }, pto: 'denied' })
    } else if (decision.status !== 'denied') {
      mark(decision.date, { pto: decision.status })
    }
  }
  for (const taken of result?.takenDaysOff ?? []) {
    if (workingOn.has(taken.date)) {
      mark(taken.date, { origin: { kind: 'against', at: null, note: `Your ${PRIORITY_LABELS[taken.priority]} day off was taken to cover your unit.` } })
    }
  }
  for (const lost of result?.lost ?? []) {
    if (!workingOn.has(lost.date)) {
      mark(lost.date, { lost: true })
    }
  }
  for (const origin of board?.origins ?? []) {
    mark(origin.date, { origin: { kind: origin.kind, at: origin.at, note: null } })
  }
  for (const callout of bridgeCallouts) {
    if (callout.result === 'yours') {
      mark(callout.shift.date, { origin: { kind: 'bridge', at: callout.filledAt, note: null } })
    }
  }

  const recalls = new Map((board?.recalls ?? []).map((seat) => [seat.shiftId, seat]))
  for (const removal of board?.removed ?? []) {
    if (!workingOn.has(removal.date)) {
      const recall = recalls.get(removal.shiftId)
      mark(removal.date, { removed: { ...removal, offeredBack: recall !== undefined, recallUntil: recall?.recallUntil ?? null } })
    }
  }
  for (const posting of board?.postings ?? []) {
    if (posting.phase === 'offers' && !workingOn.has(posting.shift.date)) {
      mark(posting.shift.date, { offered: true })
    }
  }

  const home = board?.homeUnitId ?? null
  if (home !== null) {
    for (const entry of held) {
      if (entry.unitId !== home) {
        mark(entry.date, { awayUnitId: entry.unitId })
      }
    }
  }
  return [...marks.values()].sort((a, b) => a.date.localeCompare(b.date))
}

/** A unit's short form for a calendar day: the last word of its name ("Unit B" is "B"), at most three letters. */
export function unitShort(unitId: string, names: ReadonlyMap<string, string>): string {
  const name = names.get(unitId) ?? unitId
  return (name.split(/[\s-]+/).filter(Boolean).at(-1) ?? name).slice(0, 3).toUpperCase()
}

/** How the day came to the worker, in their words. */
export const ORIGIN_LABEL: Record<DayMarkKind, string> = {
  picked_up: 'Picked up',
  assigned: 'Assigned by your supervisor',
  swapped_in: 'Swapped in',
  moved: 'Moved from another shift',
  recalled: 'Taken back after low census',
  bridge: 'Clinical Bridge callout you took',
  against: 'Worked against your ask',
}

/** Why a shift left the worker's schedule, in their words. */
export const REMOVED_LABEL: Record<DayRemoval['kind'], string> = {
  vacated: 'Taken off by your facility',
  swapped_away: 'Swapped away',
  flexed_off: 'Released for low census',
}
